"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import * as S from "./styles";
import LabelInput from "@/components/label";
import Spinner from "@/components/spinner/loading";

interface Option {
  value: string | number;
  label: string;
}

interface SelectWithSearchAsyncProps {
  label?: string;
  placeholder?: string;
  name?: string;
  onChange?: (value: string | number) => void;
  value?: string | number;
  onSearch?: (searchTerm: string, perPage?: number) => Promise<Option[]>;
  debounceMs?: number;
  minCharsToSearch?: number;
  loadingText?: string;
  noOptionsText?: string;
  initialOptions?: Option[];
  perPage?: number;
  loadInitialData?: boolean;
  resetOnValueChange?: boolean;
}

const SelectWithSearchAsync: React.FC<SelectWithSearchAsyncProps> = ({
  label,
  placeholder,
  name,
  onChange,
  value,
  onSearch,
  debounceMs = 500,
  minCharsToSearch = 2,
  loadingText = "Buscando...",
  noOptionsText = "Nenhuma opção encontrada",
  initialOptions = [],
  perPage = 15,
  loadInitialData = true,
  resetOnValueChange = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const [selected, setSelected] = useState<Option | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState<Option[]>(initialOptions);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;

      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          console.log("debouncedSearch executado com:", {
            searchTerm,
            minCharsToSearch,
          });

          if (searchTerm.trim().length >= minCharsToSearch && onSearch) {
            console.log("Iniciando busca...");
            setLoading(true);
            setHasSearched(true);
            try {
              const results = await onSearch(searchTerm.trim(), perPage);
              console.log("Resultados recebidos:", results);
              setOptions(results);
            } catch (error) {
              console.error("Erro na busca:", error);
              setOptions([]);
            } finally {
              setLoading(false);
            }
          } else if (searchTerm.trim().length === 0) {
            console.log("Resetando para opções iniciais");
            setOptions([]);
            setHasSearched(false);
          } else {
            console.log("Termo de busca muito curto, limpando opções");
            setOptions([]);
            setHasSearched(false);
          }
        }, debounceMs);
      };
    })(),
    [onSearch, debounceMs, minCharsToSearch, perPage],
  );

  useEffect(() => {
    if (value !== undefined && value !== null && value !== "") {
      const matched = options.find(
        (option) => String(option.value) === String(value),
      );
      if (matched && (!selected || String(selected.value) !== String(value))) {
        setSelected(matched);
        setSearchValue(matched.label);
      }
    } else if (value === "" || value === null || value === undefined) {
      if (selected !== null) {
        setSelected(null);
        setSearchValue("");
      }
      // Reset search state when value is cleared (e.g., when modal is closed)
      if (
        resetOnValueChange &&
        (value === "" || value === null || value === undefined)
      ) {
        setHasSearched(false);
        setOptions([]);
      }
    }
  }, [value]);

  const handleSelect = (option: Option) => {
    setSelected(option);
    setSearchValue(option.label);
    setIsOpen(false);
    if (onChange) {
      onChange(option.value);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    setSearchValue(newValue);
    setIsOpen(true);

    if (selected && newValue !== selected.label) {
      setSelected(null);
      if (onChange) {
        onChange("");
      }
    }

    debouncedSearch(newValue);
  };

  const handleInputClick = async () => {
    setIsOpen(!isOpen);

    if (!isOpen && !hasSearched && onSearch && loadInitialData) {
      setLoading(true);
      try {
        const results = await onSearch("", perPage);
        setOptions(results);
        setHasSearched(true);
      } catch (error) {
        console.error("Erro na busca inicial:", error);
        setOptions(initialOptions);
      } finally {
        setLoading(false);
      }
    } else if (!hasSearched && initialOptions.length > 0) {
      setOptions(initialOptions);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSelected(null);
        setSearchValue("");
        if (onChange) {
          onChange("");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onChange]);

  const shouldShowMinCharsMessage =
    searchValue.trim().length > 0 &&
    searchValue.trim().length < minCharsToSearch &&
    !loading;

  return (
    <>
      {label && <LabelInput labelInput={label} />}
      <S.BoxSelect ref={ref}>
        <S.SelectComponent
          as="input"
          autoComplete="off"
          type="text"
          name={name}
          value={searchValue}
          onClick={handleInputClick}
          onChange={handleInputChange}
          placeholder={placeholder}
        />
        <S.IconComponent name="chevronDown" />

        {isOpen && (
          <S.OptionsList>
            {loading ? (
              <S.OptionItem $isDisabled>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                  >
                    <Spinner />
                  </div>
                  {loadingText}
                </div>
              </S.OptionItem>
            ) : shouldShowMinCharsMessage ? (
              <S.OptionItem $isDisabled>
                Digite pelo menos {minCharsToSearch} caracteres para buscar
              </S.OptionItem>
            ) : options.length > 0 ? (
              options.map((option) => (
                <S.OptionItem
                  key={option.value}
                  onClick={() => handleSelect(option)}
                  $isSelected={selected?.value === option.value}
                >
                  {option.label}
                </S.OptionItem>
              ))
            ) : hasSearched || searchValue.trim().length >= minCharsToSearch ? (
              <S.OptionItem $isDisabled>{noOptionsText}</S.OptionItem>
            ) : null}
          </S.OptionsList>
        )}
      </S.BoxSelect>
    </>
  );
};

export default SelectWithSearchAsync;
